import { Link } from 'react-router-dom';
import { statusLabels } from '@/mocks/comics';

interface ComicCardProps {
  comic: {
    id: number;
    slug: string;
    title: string;
    author: string;
    coverImage: string;
    category: string;
    categorySlug: string;
    status: string;
    rating: number;
    views: number;
    chapters: number;
    updateDate: string;
    tags?: string[];
  };
  variant?: 'default' | 'compact' | 'featured';
}

function formatViews(views: number): string {
  if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toLocaleString();
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const isFeatured = variant === 'featured';
  const isCompact = variant === 'compact';

  if (isCompact) {
    return (
      <Link
        to={`/comic/${comic.slug}`}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-200/60 transition-colors group"
      >
        <div className="w-14 h-18 shrink-0 rounded-md overflow-hidden bg-background-300 flex-shrink-0">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h5 className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h5>
          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-secondary-600">{comic.category}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-accent-100/80 text-accent-700 font-medium">
              {comic.rating}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (isFeatured) {
    return (
      <Link
        to={`/comic/${comic.slug}`}
        className="block rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/60 transition-all duration-300 group h-full"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
              comic.status === 'serializing'
                ? 'bg-accent-500 text-background-50'
                : 'bg-background-50/90 text-foreground-800'
            }`}>
              {statusLabels[comic.status]}
            </span>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center gap-3 mt-3">
            <span className="flex items-center gap-1 text-xs text-secondary-500">
              <i className="ri-star-fill text-amber-400"></i>
              {comic.rating}
            </span>
            <span className="text-xs text-foreground-400">
              {formatViews(comic.views)} 阅读
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.slug}`}
      className="block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/60 transition-all duration-300 group h-full"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={comic.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2">
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
            comic.status === 'serializing'
              ? 'bg-accent-500 text-background-50'
              : 'bg-background-50/90 text-foreground-800'
          }`}>
            {statusLabels[comic.status]}
          </span>
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {comic.title}
        </h4>
        <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="flex items-center gap-1 text-xs text-secondary-500">
            <i className="ri-star-fill text-amber-400"></i>
            {comic.rating}
          </span>
          <span className="text-xs text-foreground-400">共 {comic.chapters} 话</span>
        </div>
        {comic.tags && comic.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {comic.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}